/**
 * Frontend entry. Boots TanStack Router, TanStack Query, and the theme
 * watcher.
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { registerSW } from "virtual:pwa-register";
import { routeTree } from "./routeTree.gen";
import { ThemeProvider } from "./lib/theme";
import { ToastProvider } from "./components/toast";
import { ConfirmProvider } from "./components/confirm";
import "./app.css";

// PWA updates. An installed PWA only checks for a new service worker on a fresh
// navigation — which basically never happens when you just reopen or resume the
// app — so it can sit on an old build indefinitely. We force the check on a
// timer, whenever the app is brought to the foreground, and when connectivity
// returns, then auto-apply (skip-waiting + reload). The reload is deferred while
// the user is actively typing so a background update can't interrupt an edit.
if (typeof window !== "undefined") {
  const CHECK_INTERVAL_MS = 30 * 60 * 1000; // every 30 min while open
  let applyPending = false;

  const isEditing = () => {
    const el = document.activeElement as HTMLElement | null;
    return (
      !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable === true)
    );
  };

  const updateSW = registerSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      const check = () => {
        if (!document.hidden) void registration.update().catch(() => {});
      };
      setInterval(check, CHECK_INTERVAL_MS);
      window.addEventListener("online", check);
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) return;
        check();
        // A version that became ready while we were away applies on return.
        if (applyPending) {
          applyPending = false;
          void updateSW(true);
        }
      });
    },
    onNeedRefresh() {
      // New version is ready. Apply now unless mid-edit — then wait for the next
      // foreground so we never reload out from under active typing.
      if (isEditing()) {
        applyPending = true;
        return;
      }
      void updateSW(true);
    },
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      // 5 minutes. Note/folder mutations explicitly invalidate, so the cache
      // never goes out of sync — the long staleTime just means "don't refetch
      // identical data every time the user navigates back to a page."
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  // Preload route chunks + query data on link hover/touch.
  defaultPreload: "intent",
  // Honor the preloaded data on actual navigation instead of treating it as
  // stale and refetching. Was 0, which made the preload pointless — every
  // click triggered a fresh fetch even though we'd just prepared the cache
  // on hover. 30s aligns with feeling "fresh enough" for a notes app.
  defaultPreloadStaleTime: 30_000,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <ConfirmProvider>
            <RouterProvider router={router} />
          </ConfirmProvider>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
